import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API, api, getSessionId, getToken } from "@/lib/api";
import { useCountry } from "@/lib/country";
import { track, trackOnce, EVENTS } from "@/lib/analytics";
import axios from "axios";
import { MessageCircle, Send, Sparkles, BookmarkPlus, Check, MapPin } from "lucide-react";
import { toast } from "sonner";
import { detectTransition } from "@/lib/transitionDetect";
import { PageHero } from "@/components/PageHero";

const GREETING = "Hi, I'm Lane. I'm here to walk this with you — gently, at your pace. What's happening today?";

const STEP_HEADERS = new Set([
    "what you're experiencing",
    "why this happens",
    "what to try today",
    "when to contact the healthcare team",
    "when to contact your healthcare team",
    "what usually comes next",
    "helpful resources",
    "save to care team summary",
    "caregiver check-in",
    "caregiver check in",
    "day-of + first-week transition plan:",
]);

// Detect a "Day-of + first-week transition plan" block in Lane's reply and
// extract the two bullet lists. Returns null if no plan is in the text.
function extractTransitionPlan(text) {
    if (!text || !/Day-?of\s*\+\s*first-?week|transition plan/i.test(text)) return null;
    // Pull lines between "Day-of:" and the next labelled section, then between
    // "First week:" and the end-of-block.
    const lines = text.split("\n");
    const startIdx = lines.findIndex(l => /^\s*\*?\*?day-?of:?\*?\*?\s*$/i.test(l.trim()));
    const weekIdx = lines.findIndex(l => /^\s*\*?\*?first\s*week:?\*?\*?\s*$/i.test(l.trim()));
    if (startIdx === -1 || weekIdx === -1 || weekIdx <= startIdx) return null;
    const grab = (from, to) => lines.slice(from + 1, to)
        .map(l => l.replace(/^\s*[-•*]\s*/, "").trim())
        .filter(l => l && !/^[\W]+$/.test(l));
    const day_of = grab(startIdx, weekIdx);
    // First week runs until a blank line, a markdown link, or EOF.
    let endIdx = lines.length;
    for (let i = weekIdx + 1; i < lines.length; i++) {
        const t = lines[i].trim();
        if (t === "" && i > weekIdx + 2) { endIdx = i; break; }
        if (/^\[.+\]\(\/.+\)/.test(t)) { endIdx = i; break; }
    }
    const first_week = grab(weekIdx, endIdx);
    if (!day_of.length && !first_week.length) return null;
    return { day_of, first_week };
}

const HELP_TOPICS = [
    { v: "Agitation or restlessness", icon: "💢" },
    { v: "Wandering", icon: "🚪" },
    { v: "Sleep problems", icon: "🌙" },
    { v: "Refusal of care", icon: "🛁" },
    { v: "I'm overwhelmed", icon: "❤️" },
    { v: "Family conflict", icon: "👥" },
    { v: "Questions for the doctor", icon: "🩺" },
    { v: "Aggression", icon: "⚠️" },
    { v: "Hallucinations", icon: "👁️" },
    { v: "Appetite changes", icon: "🍽️" },
];

// Inline renderer for a single line — links, bold, code-stripped.
function renderInline(text, keyPrefix = "") {
    if (!text) return null;
    const regex = /\[([^\]]+)\]\((\/[^)]+)\)|\*\*([^*]+)\*\*/g;
    const parts = [];
    let last = 0;
    let m;
    let i = 0;
    while ((m = regex.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        if (m[1] && m[2]) {
            parts.push(
                <Link key={`${keyPrefix}-lk-${i++}`} to={m[2]}
                    className="text-teal-dark font-medium underline underline-offset-2 hover:text-teal">
                    {m[1]}
                </Link>
            );
        } else if (m[3]) {
            parts.push(
                <strong key={`${keyPrefix}-b-${i++}`} className="font-semibold text-[#1F2624]">{m[3]}</strong>
            );
        }
        last = regex.lastIndex;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
}

// Lane reply renderer — recognizes the 8-step framework section headings
// (e.g. **What you're experiencing**) and renders them as styled block
// headers with the supporting paragraph(s) underneath. Falls back to plain
// markdown-with-links rendering for anything else (older replies, partial
// streams).
function renderWithLinks(text) {
    if (!text) return null;
    const lines = text.split("\n");
    const blocks = [];
    let buffer = [];
    const flush = (key) => {
        if (!buffer.length) return;
        const para = buffer.join("\n").trim();
        if (!para) { buffer = []; return; }
        blocks.push(
            <p key={`p-${key}`} className="whitespace-pre-wrap leading-relaxed">
                {renderInline(para, `p-${key}`)}
            </p>
        );
        buffer = [];
    };
    lines.forEach((raw, idx) => {
        const line = raw.trimEnd();
        const headerMatch = line.trim().match(/^\*\*(.+?)\*\*\s*$/);
        if (headerMatch && STEP_HEADERS.has(headerMatch[1].trim().toLowerCase())) {
            flush(`b-${idx}`);
            blocks.push(
                <h4
                    key={`h-${idx}`}
                    data-testid="lane-step-header"
                    className="mt-3 first:mt-0 text-xs uppercase tracking-wider font-semibold text-teal-dark"
                >
                    {headerMatch[1]}
                </h4>
            );
            return;
        }
        buffer.push(raw);
    });
    flush("tail");
    return blocks;
}

export default function Chat() {
    const sid = getSessionId();
    const { language } = useCountry();
    const [params] = useSearchParams();
    const topicParam = params.get("topic") || "";
    const helpMode = topicParam === "help";
    // Pre-seed message comes from `?prompt=` (decoded, plain text) OR from
    // localStorage 'ml_lane_seed_prompt' (set by SEO article pages so the
    // seed survives an auth redirect from a public article to /chat).
    const seedPrompt = params.get("prompt") || (typeof window !== "undefined" ? (localStorage.getItem("ml_lane_seed_prompt") || "") : "");
    const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [showTopics, setShowTopics] = useState(true);
    const [autoSent, setAutoSent] = useState(false);
    const [followup, setFollowup] = useState(null);
    const scrollRef = useRef(null);
    const composerRef = useRef(null);

    useEffect(() => {
        // Mobile: the PageHero pushes the composer below the fold — bring the
        // whole chat card (incl. composer) into view on arrival.
        if (window.matchMedia("(max-width: 767px)").matches) {
            const t = setTimeout(() => {
                document.querySelector('[data-testid="chat-container"]')
                    ?.scrollIntoView({ block: "end", behavior: "auto" });
            }, 250);
            return () => clearTimeout(t);
        }
    }, []);

    // Focus the composer on arrival — caregivers land ready to type.
    useEffect(() => {
        const t = setTimeout(() => composerRef.current?.focus(), 600);
        return () => clearTimeout(t);
    }, []);

    // Auto-grow the composer to fit its content (up to 120px) so prefills
    // from the check-in handoff are never clipped.
    useEffect(() => {
        const el = composerRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }, [input]);

    useEffect(() => {
        const token = getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        axios.get(`${API}/ai/history/${sid}`, { headers }).then(r => {
            if (r.data && r.data.length) {
                setMessages([{ role: "assistant", content: GREETING }, ...r.data.map(m => ({ role: m.role, content: m.content }))]);
                setShowTopics(false);
            }
        }).catch(() => { /* no history yet — fine */ });
    }, [sid]);

    // One "session opened" event per browser session (trackOnce dedupes
    // StrictMode double-mounts and tab toggling — audit fix, Jul 2026).
    useEffect(() => {
        trackOnce("ask_lane_started", EVENTS.ASK_LANE_STARTED, {
            source: helpMode ? "help_mode" : "chat_entry",
            seeded: Boolean(seedPrompt),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, streaming]);

    // Pre-fill (NEVER auto-send) a seeded prompt from URL or localStorage —
    // the caregiver may have tapped a chip by accident and must be able to
    // edit or back out before anything is committed. Composer is focused
    // with the send button visible. Consumes the localStorage seed.
    useEffect(() => {
        if (!seedPrompt || autoSent) return;
        setAutoSent(true);
        if (typeof window !== "undefined") {
            localStorage.removeItem("ml_lane_seed_prompt");
        }
        setInput(seedPrompt);
        setShowTopics(false);
    }, [seedPrompt, autoSent]);

    const send = async (override) => {
        const text = ((typeof override === "string" ? override : input) || "").trim();
        if (!text || streaming) return;        if (typeof override !== "string") setInput("");
        setShowTopics(false);
        // Fire once per user-initiated turn — separate from "ask_lane_started",
        // which only fires when the chat session is first opened from a CTA.
        track(EVENTS.ASK_LANE_CONVERSATION, {
            message_length: text.length,
            session_id: sid,
            help_mode: helpMode,
        });
        // Funnel: first-ever Lane conversation for this account.
        try {
            const k = `mlcn_first_ai_${sid}`;
            if (!localStorage.getItem(k)) {
                localStorage.setItem(k, "1");
                track(EVENTS.FIRST_AI_CONVERSATION, { help_mode: helpMode });
            }
        } catch { /* storage disabled */ }
        setMessages(m => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
        setStreaming(true);
        try {
            const token = getToken();
            const res = await fetch(`${API}/ai/help`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ session_id: sid, message: text, language }),
            });
            if (!res.body) throw new Error("No stream body");
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buffer.indexOf("\n\n")) !== -1) {
                    const chunk = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);
                    if (chunk.startsWith("data: ")) {
                        const data = chunk.slice(6);
                        if (data === "[DONE]") continue;
                        // Try to JSON-decode; backend sends chunks as JSON strings so newlines
                        // inside content don't break SSE framing.
                        let decoded;
                        try {
                            decoded = JSON.parse(data);
                        } catch {
                            decoded = data;
                        }
                        if (typeof decoded === "string" && decoded.startsWith("[error]")) {
                            setMessages(m => {
                                const last = m[m.length - 1];
                                return [...m.slice(0, -1), { ...last, content: last.content + "\n\nSorry — I hit an error. Please try again." }];
                            });
                            continue;
                        }
                        setMessages(m => {
                            const last = m[m.length - 1];
                            return [...m.slice(0, -1), { ...last, content: last.content + decoded }];
                        });
                    }
                }
            }
        } catch (e) {
            setMessages(m => {
                const last = m[m.length - 1];
                return [...m.slice(0, -1), { ...last, content: "I couldn't reach Lane right now. Please try again in a moment." }];
            });
        } finally {
            setStreaming(false);
            // Tomorrow's hook — Lane's quiet promise to pick the story back
            // up. Extracted server-side after every 2nd exchange.
            setTimeout(() => {
                api.get(`/lane/followup/${sid}`).then(r => {
                    if (r.data?.line) setFollowup(r.data);
                }).catch(() => {});
            }, 4000);
            // Mark this user→assistant turn as completed. Distinct from
            // ASK_LANE_CONVERSATION (per outbound user message) — this
            // fires once the reply is fully streamed in. Used for
            // "average exchanges per session" and follow-up tracking.
            track(EVENTS.CONVERSATION_COMPLETED, {
                session_id: sid,
                message_length: text.length,
            });
            // If this isn't the user's first turn this session, it's a
            // "follow-up" — separate signal that the caregiver kept
            // engaging rather than bouncing.
            const turn = (parseInt(sessionStorage.getItem("ml_ga_lane_turns") || "0", 10) || 0) + 1;
            sessionStorage.setItem("ml_ga_lane_turns", String(turn));
            if (turn >= 2) {
                track(EVENTS.LANE_FOLLOWUP_COMPLETED, { turn });
            }
        }
    };

    return (
        // data-clarity-mask="true" — hard belt-and-suspenders. Even if the
        // route-level Clarity kill-switch fails, every DOM node inside this
        // subtree is masked in session replay. See HEALTH_DATA_ROUTES in
        // /lib/consent.js for the primary defense.
        <div data-clarity-mask="true" className="space-y-6 animate-fade-in-up">
            <PageHero
                testId="chat-header"
                kicker="Lane · your AI companion"
                kickerIcon={MessageCircle}
                title={helpMode ? "Lane is here." : "Talk to Lane"}
                subtitle={helpMode
                    ? "Take a breath. Tell me what's happening — I'll help you sort through it."
                    : "Lane is a gentle AI companion — not a doctor. For emergencies call 911, or 988 for crisis."}
            />

            <div className="ml-card overflow-hidden flex flex-col" style={{ height: "min(70vh, 640px)" }} data-testid="chat-container">
                <div
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto p-5 md:p-6 space-y-4 bg-cream"
                    data-testid="chat-messages"
                    data-clarity-mask="true"
                >
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className="max-w-[85%]">
                                <div
                                    data-testid={`chat-msg-${m.role}-${i}`}
                                    className={`rounded-2xl px-4 py-3 leading-relaxed ${m.role === "user"
                                        ? "bg-teal text-white rounded-br-md whitespace-pre-wrap"
                                        : "bg-white border border-[#E2E8F0] text-[#1F2624] rounded-bl-md space-y-2"
                                        }`}
                                >
                                    {m.role === "assistant"
                                        ? (m.content
                                            ? renderWithLinks(m.content)
                                            : (streaming && i === messages.length - 1 ? "…" : ""))
                                        : m.content}
                                </div>
                                {m.role === "assistant" && !streaming && m.content && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        <SavePlanCta text={m.content} sid={sid} />
                                        <SaveToCareTeamCta sid={sid} />
                                        <RecordChangeCta userText={messages[i - 1]?.role === "user" ? messages[i - 1].content : ""} sid={sid} />
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Topic chips — shown only at start of fresh conversation */}
                    {showTopics && messages.length === 1 && (
                        <div className="pt-3" data-testid="lane-topic-chips">
                            <p className="text-xs uppercase tracking-wider font-semibold text-[#4C5551] mb-2.5 flex items-center gap-2">
                                <Sparkles size={14} strokeWidth={1.6} className="text-teal" />
                                {helpMode ? "What's happening right now?" : "Or tap one to start"}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {HELP_TOPICS.map(t => (
                                    <button
                                        key={t.v}
                                        type="button"
                                        onClick={() => send(t.v)}
                                        disabled={streaming}
                                        data-testid={`lane-topic-${t.v.replace(/[^a-z]/gi, '-').toLowerCase()}`}
                                        className="px-3.5 py-2 rounded-2xl text-sm font-medium bg-white border border-teal-soft hover:bg-teal-soft hover:border-teal text-[#1F2624] transition-colors"
                                    >
                                        <span className="mr-1.5">{t.icon}</span>{t.v}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {followup?.line && !streaming && (
                    <div className="mx-3 md:mx-4 mb-2 px-4 py-2.5 rounded-2xl bg-teal-soft/60 border border-teal-light/60 text-sm text-teal-dark flex items-start gap-2" data-testid="lane-followup-line">
                        <span className="font-heading font-semibold shrink-0">Lane:</span>
                        <span>{followup.line}</span>
                    </div>
                )}

                <div className="border-t border-[#E2E8F0] p-3 md:p-4 bg-white flex gap-2">
                    <textarea
                        ref={composerRef}
                        data-testid="chat-input"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder="Tell Lane what's on your mind…"
                        rows={1}
                        style={{ maxHeight: 120 }}
                        className="ml-input !min-h-[48px] resize-none py-3"
                    />
                    <button
                        onClick={send}
                        disabled={streaming || !input.trim()}
                        data-testid="chat-send"
                        aria-label="Send your message to Lane"
                        className="ml-btn-primary disabled:opacity-50 !min-h-[48px] !px-5"
                    >
                        <Send size={18} strokeWidth={1.6} />
                        <span className="hidden sm:inline">{streaming ? "…" : "Send"}</span>
                    </button>
                </div>
                {/* Quiet, friendly disclaimer — caregivers see it once and
                    feel informed, not warned. */}
                <p
                    className="text-[11px] text-[#6E7A74] leading-relaxed px-4 pb-3 pt-1 text-center bg-white border-t border-cream-dark/60"
                    data-testid="chat-disclaimer"
                >
                    Lane is here to help you learn and prepare — not a substitute for your loved one&apos;s clinicians. Memory Lane is in beta and not yet HIPAA-compliant.
                </p>
            </div>
        </div>
    );
}


function SavePlanCta({ text, sid }) {
    const plan = extractTransitionPlan(text);
    const [saved, setSaved] = useState(false);
    const [saving, setSaving] = useState(false);
    if (!plan) return null;

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/transition-plans", {
                session_id: sid,
                title: "Transition plan (from Lane)",
                kind: "moving",
                day_of: plan.day_of,
                first_week: plan.first_week,
                raw_chat_excerpt: text.slice(0, 4000),
            });
            setSaved(true);
            toast.success("Saved. Find it on your Patient profile.");
        } catch {
            toast.error("Couldn't save the plan. Try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <button
            type="button"
            onClick={save}
            disabled={saved || saving}
            data-testid="chat-save-plan"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 min-h-[44px] rounded-2xl text-xs font-medium border transition-colors ${saved
                ? "bg-stage_early-light text-stage_early-dark border-stage_early"
                : "bg-white text-teal-dark border-teal hover:bg-teal-soft"
                }`}
        >
            {saved
                ? (<><Check size={14} strokeWidth={2} /> Saved to Patient profile</>)
                : (<><BookmarkPlus size={14} strokeWidth={1.8} /> {saving ? "Saving…" : "Save this plan"}</>)}
        </button>
    );
}

function SaveToCareTeamCta({ sid }) {
    const [saved, setSaved] = useState(false);
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            await api.post(`/care-team/summary/${sid}/generate`);
            setSaved(true);
            toast.success("Added to your Care Team Summary.");
        } catch {
            toast.error("Couldn't save right now.");
        } finally {
            setSaving(false);
        }
    };
    return (
        <button
            type="button"
            onClick={save}
            disabled={saved || saving}
            data-testid="chat-save-to-care-team"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 min-h-[44px] rounded-2xl text-xs font-medium border transition-colors ${saved
                ? "bg-stage_late/30 text-stage_late-dark border-stage_late/60"
                : "bg-white text-stage_late-dark border-stage_late/60 hover:bg-stage_late/20"
                }`}
        >
            {saved
                ? (<><Check size={14} strokeWidth={2} /> Saved to Care Team Summary</>)
                : (<><BookmarkPlus size={14} strokeWidth={1.8} /> {saving ? "Saving…" : "Save to Care Team Summary"}</>)}
        </button>
    );
}

/**
 * When the caregiver's message mentions a care change (fall, ER visit,
 * medication change, move, …), offer to pin it on the roadmap in one tap.
 * Nothing is recorded without the caregiver's confirmation.
 */
function RecordChangeCta({ userText, sid }) {
    const [saved, setSaved] = useState(false);
    const [saving, setSaving] = useState(false);
    const detected = detectTransition(userText);
    if (!detected) return null;

    const save = async () => {
        setSaving(true);
        try {
            await api.post(`/roadmap/transitions/${sid}`, {
                event_type: detected.event_type,
                notes: String(userText).slice(0, 300),
            });
            setSaved(true);
            toast.success("Recorded on your roadmap.", {
                action: { label: "View roadmap", onClick: () => { window.location.href = "/roadmap"; } },
            });
        } catch {
            toast.error("Couldn't record it right now.");
        } finally {
            setSaving(false);
        }
    };
    return (
        <button
            type="button"
            onClick={save}
            disabled={saved || saving}
            data-testid="chat-record-change"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 min-h-[44px] rounded-2xl text-xs font-medium border transition-colors ${saved
                ? "bg-[#F3EDFC] text-[#6D4BC0] border-[#D9C8F8]"
                : "bg-white text-[#6D4BC0] border-[#D9C8F8] hover:bg-[#F3EDFC]"
                }`}
        >
            {saved
                ? (<><Check size={14} strokeWidth={2} /> Pinned to your roadmap</>)
                : (<><MapPin size={14} strokeWidth={1.8} /> {saving ? "Recording…" : `Record on roadmap: ${detected.label}`}</>)}
        </button>
    );
}
